import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/FadeIn';
import { ArrowLeftIcon, ArrowRightIcon, SearchIcon, FlaskConicalIcon, TrendingUpIcon, LandmarkIcon, BuildingIcon, WalletIcon, HeartPulseIcon, ShieldCheckIcon, GitBranchIcon, ServerCogIcon, ScaleIcon } from 'lucide-react';
const methodPillars = [{
  title: 'Spec-driven development',
  description: 'Work starts from precise, machine-readable intent so AI produces reviewable output instead of guesswork.',
  icon: GitBranchIcon
}, {
  title: 'Purposeful MCP orchestration',
  description: 'Model Context Protocol servers wired deliberately into your delivery toolchain, not bolted on per developer.',
  icon: ServerCogIcon
}, {
  title: 'AI-assisted architecture governance',
  description: 'Architecture review moves upstream, with AI enforcing patterns rather than reviewers catching drift late.',
  icon: ScaleIcon
}, {
  title: 'Secure-by-design pipelines',
  description: 'Threat modeling and regulatory requirements are engineered into the workflow from the first commit.',
  icon: ShieldCheckIcon
}];
const engagementPhases = [{
  phase: 'Assessment',
  icon: SearchIcon,
  description: 'A focused review of your current SDLC, tooling, security posture, and team readiness, producing an AI-DLC roadmap with prioritized opportunities and projected impact.'
}, {
  phase: 'Pilot',
  icon: FlaskConicalIcon,
  description: 'AI-DLC applied to a live project line within your organization, with MCP infrastructure, governance patterns, and threat-modeling practices established. Measured before-and-after results.'
}, {
  phase: 'Scale',
  icon: TrendingUpIcon,
  description: 'Rollout across teams, supported by playbooks, training, architecture review board integration, and leadership coaching, so the capability lives inside your organization rather than depending on ours.'
}];
const audiences = [{
  label: 'Banking',
  icon: LandmarkIcon
}, {
  label: 'Credit Unions',
  icon: BuildingIcon
}, {
  label: 'Fintech',
  icon: WalletIcon
}, {
  label: 'Healthtech',
  icon: HeartPulseIcon
}];
export function AiNativeEngineering() {
  return <>
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden" data-id="element-716" data-mp="3cmieg8bv2wv5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 z-0" data-id="element-717" data-mp="3ckp7st190cn5"></div>
        <div className="absolute inset-0 bg-radial-glow z-0" data-id="element-718" data-mp="37p9uq3b2ekrs"></div>
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-blue/5 rounded-full blur-3xl animate-pulse-slow" data-id="element-719" data-mp="3mcuxfw412hz6"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-720" data-mp="3hl345dthly5c">
          <FadeIn data-id="element-721" data-mp="2tw0qeh1z4pg4">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-teal transition-colors mb-10" data-id="element-722" data-mp="2tvik0rllkuuh">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-723" data-mp="14en1t2wl6r5x" />
              Back to overview
            </Link>
          </FadeIn>

          <div className="max-w-4xl" data-id="element-724" data-mp="196wxkzkdulg4">
            <FadeIn delay={0.05} data-id="element-725" data-mp="197y9f6983byw">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium mb-8" data-id="element-726" data-mp="veqf36trc2di">
                <ShieldCheckIcon className="w-4 h-4" data-id="element-727" data-mp="zgbpmlitk4kj" />
                <span data-id="element-728" data-mp="1n5ehsnb2xfta">AI-DLC Methodology</span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-729" data-mp="1s4hftnsn6lrr">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.05]" data-id="element-730" data-mp="1s3g21bk8rl6c">
                AI-Native Engineering{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal to-blue" data-id="element-731" data-mp="1j3t10qvfchdt">
                  Transformation
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-732" data-mp="7omhbg392wuf">
              <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-3xl leading-relaxed" data-id="element-733" data-mp="7pnr7h8j5dak">
                Faster delivery. Stronger security. Built for regulated
                environments.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-734" data-mp="i5q1kaa8osdq">
              <a href="mailto:arthur@fiqis.com?subject=AI-Native%20Engineering%20Transformation%20-%20Schedule%20a%20Conversation" className="inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-400 text-navy font-semibold px-8 py-4 rounded-md transition-colors duration-200 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)] text-lg" data-id="element-735" data-mp="d87y2zezr5d4">
                Schedule a Conversation
                <ArrowRightIcon className="w-5 h-5" data-id="element-736" data-mp="wvdy6y7h72v4" />
              </a>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* The problem / the approach */}
      <section className="py-24 bg-slate border-y border-white/5" data-id="element-737" data-mp="3hn7ruo7m982v">
        <div className="max-w-7xl mx-auto px-6" data-id="element-738" data-mp="3cnutb67duckg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20" data-id="element-739" data-mp="3cn1wawy871fi">
            <FadeIn data-id="element-740" data-mp="1s6lobqy79p4p">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-widest mb-6" data-id="element-741" data-mp="1s73qt5bggzl2">
                The gap
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-snug" data-id="element-742" data-mp="1x2j3vv1n2rgf">
                Most organizations adopted AI tools. Few changed how work
                actually flows.
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed" data-id="element-743" data-mp="2awk6x0zmj0ml">
                The result is faster typing and the same bottlenecks — while
                security, compliance, and architecture governance remain
                outside the AI conversation entirely.
              </p>
            </FadeIn>

            <FadeIn direction="left" delay={0.1} data-id="element-744" data-mp="2owl86hjswa2l">
              <p className="text-sm font-medium text-teal uppercase tracking-widest mb-6" data-id="element-745" data-mp="2ova2gc6sos8u">
                Our approach
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-snug" data-id="element-746" data-mp="uhsckiix3jan">
                We restructure the entire delivery process around AI.
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed" data-id="element-747" data-mp="1xusnzujlkuou">
                Our AI Development Lifecycle (AI-DLC) methodology treats
                security and regulatory requirements as engineering inputs from
                the start, not obligations bolted on afterward.
              </p>
            </FadeIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-16" data-id="element-748" data-mp="2gr1y66rdp13k">
            {methodPillars.map(pillar => {
            const Icon = pillar.icon;
            return <StaggerItem key={pillar.title} className="h-full" data-id="element-749" data-mp="2qgljrk6h7924">
                  <div className="bg-navy border border-white/10 rounded-2xl p-8 h-full flex flex-col" data-id="element-750" data-mp="2lisgp00gyv62">
                    <div className="mb-6 inline-flex self-start p-3 rounded-xl bg-slate border border-white/5 text-teal" data-id="element-751" data-mp="2zhs27ysoqty0">
                      <Icon className="w-6 h-6" strokeWidth={1.5} data-id="element-752" data-mp="2uk9rc2v3y4nd" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-753" data-mp="3e61w9q994q29">
                      {pillar.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed text-[15px]" data-id="element-754" data-mp="3j3br5lfettxh">
                      {pillar.description}
                    </p>
                  </div>
                </StaggerItem>;
          })}
          </StaggerContainer>
        </div>
      </section>

      {/* Case study proof */}
      <section className="py-24 bg-navy relative overflow-hidden" data-id="element-755" data-mp="2vcplu1wsinr6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-teal/5 rounded-full blur-[130px] pointer-events-none" data-id="element-756" data-mp="306tucplpf3ud"></div>

        <div className="max-w-5xl mx-auto px-6 relative z-10" data-id="element-757" data-mp="2qeo3jj76usgr">
          <FadeIn data-id="element-758" data-mp="2qd35vkpkuteo">
            <div className="bg-slate border border-teal/20 rounded-3xl p-8 md:p-14 shadow-[0_0_60px_-20px_rgba(0,212,170,0.2)]" data-id="element-759" data-mp="2cfwu4a39tuyy">
              <p className="text-sm font-medium text-teal uppercase tracking-widest mb-8" data-id="element-760" data-mp="2hdpzx20nwmyp">
                Case study
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-16 items-start" data-id="element-761" data-mp="2wuogwge3y2az">
                <div data-id="element-762" data-mp="2rvv46q6nafbv">
                  <p className="text-6xl md:text-7xl font-bold text-white leading-none mb-3" data-id="element-763" data-mp="2mybg6yk5ao6q">
                    97%
                  </p>
                  <p className="text-teal font-semibold" data-id="element-764" data-mp="2iw82e3a077ia">
                    Reduction in delivery time
                  </p>
                </div>

                <div data-id="element-765" data-mp="2976oqjqxl27h">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-5 leading-snug" data-id="element-766" data-mp="296ol321xvlpj">
                    Global health-and-wellness e-commerce enterprise
                  </h2>
                  <p className="text-lg text-gray-400 leading-relaxed mb-6" data-id="element-767" data-mp="1z9adzt1wo99w">
                    AI-DLC was implemented across the delivery organization,
                    restructuring how specifications, architecture review, and
                    security validation flowed through the lifecycle. Alongside
                    the reduction in delivery time, the rate of successfully
                    delivered projects increased sharply.
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-768" data-mp="arh7ky149w27">
                    One of four organizations where the methodology has been
                    implemented end to end.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* How we engage */}
      <section className="py-24 bg-slate border-y border-white/5" data-id="element-769" data-mp="3n69t0xh6j4ap">
        <div className="max-w-7xl mx-auto px-6" data-id="element-770" data-mp="3n4oukwrqv17k">
          <FadeIn className="max-w-3xl mb-16" data-id="element-771" data-mp="3s4tgj2bf8o56">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-772" data-mp="3s3rvq64lsna9">
              How We Engage
            </h2>
            <p className="text-xl text-gray-400" data-id="element-773" data-mp="2zhgssotiyaaa">
              Three phases, each producing evidence before the next begins.
            </p>
          </FadeIn>

          <StaggerContainer className="space-y-6" data-id="element-774" data-mp="34eh0t59xpn41">
            {engagementPhases.map((phase, index) => {
            const Icon = phase.icon;
            return <StaggerItem key={phase.phase} data-id="element-775" data-mp="3spftn0v56yis" data-mpi={index}>
                  <div className="bg-navy border border-white/10 rounded-2xl p-8 md:p-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start" data-id="element-776" data-mp="3ssbsqsiq7t8t">
                    <div className="flex items-center gap-5" data-id="element-777" data-mp="6hy3cpe1banx">
                      <span className="text-4xl font-bold text-white/15 tabular-nums" data-id="element-778" data-mp="351eps58ws69p">
                        0{index + 1}
                      </span>
                      <div className="p-3 rounded-xl bg-slate border border-white/5 text-teal" data-id="element-779" data-mp="3a094ow3r9aoj">
                        <Icon className="w-6 h-6" strokeWidth={1.5} data-id="element-780" data-mp="3exrgcu95q408" />
                      </div>
                    </div>

                    <div data-id="element-781" data-mp="3jrm0tzwl6n0d">
                      <h3 className="text-2xl font-semibold text-white mb-3" data-id="element-782" data-mp="2max1ccv3zwu5">
                        {phase.phase}
                      </h3>
                      <p className="text-gray-400 text-lg leading-relaxed max-w-3xl" data-id="element-783" data-mp="2r7x2c9d3q9ee">
                        {phase.description}
                      </p>
                    </div>
                  </div>
                </StaggerItem>;
          })}
          </StaggerContainer>
        </div>
      </section>

      {/* Who it's for + Why FiQIS */}
      <section className="py-24 bg-navy" data-id="element-784" data-mp="24t7yia03scsf">
        <div className="max-w-7xl mx-auto px-6" data-id="element-785" data-mp="24sgcnqwb5tfc">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20" data-id="element-786" data-mp="20rmpx0ulqko1">
            <FadeIn data-id="element-787" data-mp="1w0efq2lg6v9t">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-788" data-mp="1vzuuod0i3qrk">
                Who It's For
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed mb-10" data-id="element-789" data-mp="1r235vrv5kkrl">
                Engineering organizations that need measurable gains in
                delivery speed without compromising compliance, auditability,
                or security.
              </p>

              <StaggerContainer className="grid grid-cols-2 gap-4" data-id="element-790" data-mp="1d10i9aoif2ek">
                {audiences.map(audience => {
                const Icon = audience.icon;
                return <StaggerItem key={audience.label} className="flex items-center gap-3 bg-slate/50 border border-white/5 rounded-xl px-5 py-4" data-id="element-791" data-mp="17f9s86ngtoy5">
                      <Icon className="w-5 h-5 text-teal flex-shrink-0" strokeWidth={1.5} data-id="element-792" data-mp="ye1rlhxh8c0w" />
                      <span className="text-gray-300 font-medium" data-id="element-793" data-mp="1m6s1zmpqen92">
                        {audience.label}
                      </span>
                    </StaggerItem>;
              })}
              </StaggerContainer>
            </FadeIn>

            <FadeIn direction="left" delay={0.1} data-id="element-794" data-mp="1hcee05defief">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-795" data-mp="253a3uwyao3j1">
                Why FiQIS
              </h2>
              <div className="space-y-5 text-lg text-gray-400 leading-relaxed" data-id="element-796" data-mp="29ui87ekroypc">
                <p data-id="element-797" data-mp="2j8k4wbxu0lc1">
                  FiQIS was founded by the enterprise architect behind Zelle®
                  and Real-Time Payments — infrastructure integrating more than
                  2,000 financial institutions — and the co-founder and CTO of
                  a regulated BNPL platform led through a successful exit.
                </p>
                <p data-id="element-798" data-mp="2o024avk7iduw">
                  Our approach to AI-native engineering is grounded in
                  national-scale systems, defense-grade security, and the
                  practical realities of FDIC, NCUA, PCI-DSS, and SOC 2
                  environments.
                </p>
              </div>

              <div className="mt-8 bg-slate border border-white/10 rounded-xl p-6" data-id="element-799" data-mp="2a9f8bb5tz22i">
                <p className="text-sm text-gray-500 mb-2" data-id="element-800" data-mp="257g1tsesnp1c">
                  Methodology documented in
                </p>
                <p className="text-white font-semibold" data-id="element-801" data-mp="3lpdk3mse827f">
                  AI-SDLC and the New Enterprise Architecture Approach
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 relative overflow-hidden" data-id="element-802" data-mp="1v78d6q9fkeut">
        <div className="absolute inset-0 bg-gradient-to-b from-navy to-slate z-0" data-id="element-803" data-mp="2059ub5naq43n"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20 z-0" data-id="element-804" data-mp="207mn6duldyvh"></div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-805" data-mp="2486idbvlusez">
          <FadeIn data-id="element-806" data-mp="2955bbr3j8tbl">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6" data-id="element-807" data-mp="295ohkajtlt9s">
              Ready to Move Beyond AI Experimentation?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-808" data-mp="2iv81ljj9rt68">
              Start with an assessment. You'll leave with an AI-DLC roadmap,
              prioritized opportunities, and projected impact for your delivery
              organization.
            </p>
            <a href="mailto:arthur@fiqis.com?subject=AI-Native%20Engineering%20Transformation%20-%20Schedule%20a%20Conversation" className="inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-400 text-navy font-bold px-8 py-4 rounded-md transition-colors duration-200 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)] text-lg" data-id="element-809" data-mp="2ruldsjys2vun">
              Schedule a Conversation
              <ArrowRightIcon className="w-5 h-5" data-id="element-810" data-mp="78o37v3xjyvm" />
            </a>
          </FadeIn>
        </div>
      </section>
    </>;
}